<template>
  <div
    ref="commentContainer"
    class="bg-primary flex flex-col px-4 py-3 transition-all ease-in duration-500 relative border-t border-secondary"
  >
    <div
      id="mention-container"
      class="absolute bottom-[calc(100%+4px)] w-full z-1 left-0 scrollbar-tertiary"
    />
    <div class="flex justify-between items-center">
      <CommentEditor
        ref="editor"
        v-model:contents="form.contents"
        :mentionables="mentionables"
        @focus="hasFocus = true"
      />
      <div class="self-start">
        <Button
          variant="tertiary"
          type="gray"
          size="sm"
          icon
          @click="attachmentInput?.click()"
        >
          <Icon
            name="attachment"
            class="cursor-pointer transition-colors text-tertiary"
          />
        </Button>
        <input
          ref="attachmentInput"
          type="file"
          multiple
          hidden
          @input="handleSelectAttachments"
        />
      </div>
    </div>
    <div v-show="form.attachments?.length" class="flex flex-col pt-4 gap-1">
      <File
        v-for="attachment in form.attachments"
        :key="attachment.id"
        :file="attachment.data"
        color="gray"
        remove
        @remove="handleRemoveAttachment(attachment.id)"
      />
    </div>
    <div
      v-show="showSubmit"
      class="flex items-center pt-4"
      :class="canMakePublicComments ? 'justify-between' : 'justify-end'"
    >
      <Checkbox
        v-if="canMakePublicComments"
        :label="$t('components.inputs.comment.public_label')"
        v-model:checked="form.isPublic"
      />
      <div class="flex gap-x-3">
        <Button
          :label="$t('components.buttons.cancel')"
          variant="link"
          type="gray"
          size="md"
          @click="handleCancel"
        />
        <Button
          :label="$t('components.buttons.submit')"
          variant="primary"
          size="md"
          :disabled="isEmpty(form.contents) && form.attachments.length === 0"
          @click="handleSubmit"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import axios from "axios";
import route from "ziggy-js";
import { inject, computed, ref } from "vue";
import { useForm } from "@inertiajs/vue3";
import { isEmpty, uniqueId } from "lodash";
import { Button } from "@/SharedComponents/Buttons";
import { Checkbox } from "@/SharedComponents/Checkboxes";
import { CommentEditor } from "@/SharedComponents/CommentBox";
import { File } from "@/SharedComponents/Files";
import { Icon } from "@/SharedComponents/Icon";
import { useAddComment } from "@/Composables/useActivityHistory";
import { useClickOutside } from "@/Composables/useClickOutside";

const editor = ref(null);
const attachmentInput = ref(null);

const props = defineProps({
  task: { type: Object, required: true },
  mentionables: { type: Array, default: () => [] },
});

const { hasPermission, permissions } = inject("permissions");
const canMakePublicComments = hasPermission(permissions.publicReply);

const form = useForm({
  contents: {},
  attachments: [],
  isPublic: false,
});

function handleSelectAttachments(e) {
  const files = e.target.files;
  let attachments = [];

  for (const file of files) {
    const id = uniqueId();

    attachments = [
      ...attachments,
      {
        id: id,
        name: file.name,
        size: file.size,
        type: file.type,
      },
    ];

    const document = new FormData();
    document.append("file", file, file.name);

    axios
      .post(route("api.documents.store", [props.task.tenant.id]), document)
      .then((response) => {
        form.attachments = [
          ...form.attachments,
          {
            id: id,
            documentId: response.data.data.id,
            data: file,
          },
        ];

        attachments = attachments.filter((attachment) => attachment.id !== id);
      });
  }

  // Allow selection of the same file again.
  e.target.value = null;
}

function handleRemoveAttachment(id) {
  form.attachments = form.attachments.filter(
    (attachment) => attachment.id !== id
  );
}

const { isPending: busy, mutateAsync: addComment } = useAddComment();

function handleSubmit() {
  if (busy.value) {
    return;
  }

  busy.value = true;
  addComment({
    task: props.task,
    contents: form.contents,
    type: form.isPublic ? "public_reply" : "internal",
    documents: form.attachments.map((attachment) => attachment.documentId),
  }).then(() => {
    handleCancel();
    busy.value = false;
  });
}

const commentContainer = ref(null);
const hasFocus = ref(false);

useClickOutside(commentContainer, () => {
  hasFocus.value = false;
});

function handleCancel() {
  form.reset();
  editor.value?.clear();
  hasFocus.value = false;
}

const showSubmit = computed(() => {
  return (
    !isEmpty(form.contents) ||
    form.attachments.length ||
    hasFocus.value ||
    form.isPublic
  );
});
</script>
